import { useRef, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Journalist Courses Page
 *
 * Guides users through: search, category filter, requirement filter,
 * enrollment period filter, and course card.
 */
export const useJournalistCoursesTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const courseCardRef = useRef<HTMLDivElement>(null);
  const isOnJournalistCourses = pathname === ROUTE_NAMES.JOURNALIST_COURSES;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistCourses",
      enabled: isOnJournalistCourses,
      steps: [
        {
          id: "journalist-courses-search",
          targetSelector: '[data-tour-item="journalist-courses-search"]',
          title: t("tourGuide.journalistCourses.step1.title"),
          description: t("tourGuide.journalistCourses.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        {
          id: "journalist-courses-category-filter",
          targetSelector:
            '[data-tour-item="journalist-courses-category-filter"]',
          title: t("tourGuide.journalistCourses.step2.title"),
          description: t("tourGuide.journalistCourses.step2.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        {
          id: "journalist-courses-requirement-filter",
          targetSelector:
            '[data-tour-item="journalist-courses-requirement-filter"]',
          title: t("tourGuide.journalistCourses.step3.title"),
          description: t("tourGuide.journalistCourses.step3.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        {
          id: "journalist-courses-enrollment-filter",
          targetSelector:
            '[data-tour-item="journalist-courses-enrollment-filter"]',
          title: t("tourGuide.journalistCourses.step4.title"),
          description: t("tourGuide.journalistCourses.step4.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 8,
        },
        {
          id: "journalist-courses-course-card",
          targetRef: courseCardRef,
          title: t("tourGuide.journalistCourses.step5.title"),
          description: t("tourGuide.journalistCourses.step5.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnJournalistCourses, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [{ type: TourGuideType.JournalistCourses, isDone: true }],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistCoursesTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistCourses,
    isOnTourPath: isOnJournalistCourses && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistCoursesTourGuide.isActive) {
      setActiveTourModule("journalistCourses");
      setCurrentTourStep(journalistCoursesTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistCoursesTourGuide.isActive,
    journalistCoursesTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    journalistCoursesTourGuide,
    courseCardRef,
    isMobile,
  };
};
